import { useEffect, useRef, useState } from 'react';
import { getAccessToken, getRefreshToken } from '@/auth/token-store';
import { onRefreshFailed, refreshSingleFlight } from '@/auth/refreshClient';
import { usePulseStore } from './usePulseStore';
import { SSE_RECONNECT_DELAY_MS } from '../constants';
import { consumeSseBuffer } from '../lib/parseSse';
import type { PulseEvent, PulseContentItem, PulseStats } from '../types';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '');

interface PulseStreamState {
  connected: boolean;
}

/**
 * Connects to the Pulse SSE event stream (fetch + Authorization header) and
 * hydrates the grid on every reconnect. Redis pub/sub has no replay.
 */
export function usePulseStream(): PulseStreamState {
  const [connected, setConnected] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectResolveRef = useRef<(() => void) | null>(null);
  const processEvent = usePulseStore((s) => s.processEvent);
  const setInitialGrid = usePulseStore((s) => s.setInitialGrid);

  useEffect(() => {
    let cancelled = false;

    const fetchInitialGrid = async () => {
      try {
        const token = getAccessToken();
        const res = await fetch(`${API_BASE}/admin/api/pulse/grid`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (res.status === 401 && getRefreshToken()) {
          const fresh = await refreshSingleFlight();
          const retry = await fetch(`${API_BASE}/admin/api/pulse/grid`, {
            headers: { Authorization: `Bearer ${fresh}` },
          });
          if (!retry.ok) return;
          const envelope = (await retry.json()) as { data: { items: PulseContentItem[]; stats: PulseStats } };
          if (!cancelled && envelope.data) {
            setInitialGrid(envelope.data.items ?? [], envelope.data.stats);
          }
          return;
        }
        if (!res.ok) return;
        const envelope = (await res.json()) as { data: { items: PulseContentItem[]; stats: PulseStats } };
        if (!cancelled && envelope.data) {
          setInitialGrid(envelope.data.items ?? [], envelope.data.stats);
        }
      } catch (e) {
        console.warn('[Pulse] Failed to fetch initial grid:', e);
      }
    };

    const openStream = async (signal: AbortSignal): Promise<void> => {
      const token = getAccessToken();
      const headers: Record<string, string> = {
        Accept: 'text/event-stream',
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      let res = await fetch(`${API_BASE}/admin/api/pulse/events`, { headers, signal });
      if (res.status === 401 && getRefreshToken()) {
        try {
          const fresh = await refreshSingleFlight();
          headers.Authorization = `Bearer ${fresh}`;
          res = await fetch(`${API_BASE}/admin/api/pulse/events`, { headers, signal });
        } catch {
          onRefreshFailed();
          window.location.assign('/login');
          throw new Error('pulse_auth_failed');
        }
      }
      if (!res.ok || !res.body) {
        throw new Error(`pulse_sse_http_${res.status}`);
      }
      if (cancelled) return;
      setConnected(true);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (!cancelled) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = consumeSseBuffer(buffer, (json) => {
          try {
            processEvent(JSON.parse(json) as PulseEvent);
          } catch {
            // Ignore malformed frames.
          }
        });
      }
    };

    const loop = async () => {
      while (!cancelled) {
        abortRef.current?.abort();
        const ac = new AbortController();
        abortRef.current = ac;
        await fetchInitialGrid();
        if (cancelled) return;
        try {
          await openStream(ac.signal);
        } catch (e) {
          if (cancelled || ac.signal.aborted) return;
          if (e instanceof Error && e.message === 'pulse_auth_failed') return;
          console.warn('[Pulse] SSE stream dropped:', e);
        }
        if (cancelled) return;
        setConnected(false);
        await new Promise<void>((resolve) => {
          reconnectResolveRef.current = resolve;
          reconnectTimerRef.current = setTimeout(resolve, SSE_RECONNECT_DELAY_MS);
        });
      }
    };

    void loop();

    return () => {
      cancelled = true;
      abortRef.current?.abort();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectResolveRef.current?.();
    };
  }, [processEvent, setInitialGrid]);

  return { connected };
}
